import { ExtraDataMappingType } from "@/enums/extra-data-mapping-type";
import type { LineItem, LowConfidenceItem } from "@/interfaces/estimate-detail";
import EngineSharedUtils from "../Rooms/Lines/util/engine-shared-utils";
import type { LineItemXactimateMapping } from "@/interfaces/payloads/xactimate";

export type LineItemIssue = "name" | "quantity" | "confidence" | "unit";

interface LineItemError {
  issue: LineItemIssue;
  description: string;
}

export function errorTextFromTuple(
  lineItem: LineItem,
  lowConfidenceArray?: LowConfidenceItem[],
  lineItemXactimateMapping?: LineItemXactimateMapping[],
): LineItemError[] {
  const errors: LineItemError[] = [];
  const mappingExtraData = lineItem.mappingExtraData;

  let hasInputWithNoMapping = false;
  let hasNotAllMappingsUsed = false;

  mappingExtraData?.forEach((med) => {
    switch (med.type) {
      case ExtraDataMappingType.NoEmptyValueNoDefault:
        errors.push({
          issue: "name",
          description: "Some required values were not supplied.",
        });
        break;

      case ExtraDataMappingType.InputWithNoMapping:
        if (!hasNotAllMappingsUsed && !hasInputWithNoMapping) {
          errors.push({
            issue: "name",
            description: "Some writing on the tic sheet was not recognized.",
          });
          hasInputWithNoMapping = true;
        }
        break;

      case ExtraDataMappingType.NotAllMappingsUsed:
        if (!hasInputWithNoMapping && !hasNotAllMappingsUsed) {
          errors.push({
            issue: "name",
            description: "Some tic sheet writing was not used in the line item.",
          });
          hasNotAllMappingsUsed = true;
        }
        break;

      case ExtraDataMappingType.QuantityOutOfRange:
        errors.push({
          issue: "quantity",
          description: "The quantity given in the tic sheet falls outside of the expected range.",
        });
        break;

      //   case ExtraDataMappingType.QuantityOutOfRangeTPARule:
      //     errors.push({
      //       issue: 'quantity',
      //       description: "The quantity given in the tic sheet falls outside of the TPA's allowed range.",
      //     });
      //     break;

      case ExtraDataMappingType.UnitNotMatching: {
        let errorMsg = "The units for the quantity in this line item appear incorrect";
        if (lineItemXactimateMapping) {
          const allowedUnits = EngineSharedUtils.getAllowedUnitsFromXactimateMapping(lineItem, lineItemXactimateMapping);
          if (allowedUnits.length) {
            errorMsg = `The units for the quantity in this line item appear incorrect. The allowed unit${allowedUnits.length > 1 ? "s are" : " is"} ${allowedUnits.map((unit) => `"${unit.toUpperCase()}"`).join(", ")}.`;
          }
        }

        errors.push({
          issue: "unit",
          description: errorMsg,
        });
        break;
      }

      case ExtraDataMappingType.LowConfidenceInput:
        errors.push({
          issue: "confidence",
          description: "We have low confidence in our interpretation.",
        });
        break;

      case ExtraDataMappingType.NoUnit:
        errors.push({
          issue: "unit",
          description: "The unit for the quantity in this line item is missing.",
        });
        break;

      case ExtraDataMappingType.NoQuantity:
        errors.push({
          issue: "unit",
          description: "The quantity for this line item is missing.",
        });
        break;
    }
  });

  if (lowConfidenceArray?.length) {
    errors.push({
      issue: "confidence",
      description: "We have low confidence in our interpretation.",
    });
  }

  // Remove duplicates (same issue + description)
  const uniqueErrors = errors.filter(
    (error, index, self) => index === self.findIndex((e) => e.issue === error.issue && e.description === error.description),
  );

  return uniqueErrors;
}
